import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  StyledBoxV2,
  StyledBoxButton,
  StyledBoxCard,
  StyledBoxWrapperButton,
  StyledDivider,
  StyledGrid,
  StyledMain,
  IconButtonStyle,
  StyleBoxContainer,
  StyleTableHead,
  StyleTable,
  StyleTableCell,
  StyleTableActionCell,
  StyleTableActionButtonWrap,
  StyleTableActionHeaderCell,
  StyleTableFieldWrap,
  StyleTableIconButton,
  StyledUploadButton,
  StyledUploadField,
  StyledPersonalShopperFieldGrid,
  StyledUploadWrapper,
  StyledUploadLabel,
  StyledUploadFileLabel,
  StyledUploadErrorText,
} from "./item/style";
import { Box, Button, CircularProgress, InputAdornment, TableBody, TableCell, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FieldArray, Form, Formik, getIn } from "formik";
import theme from "../../theme";
import * as Yup from "yup";
import AddRound from "../../assets/Icons/AddRound";
import { useDispatch, useSelector } from "react-redux";
import {
  ecommercePlatformOptions,
  paymentStatusOptions,
  selfDropTimeOptions,
} from "./item/constant";
import CountryStateFields from "../../components/common/country-state-fields/CountryStateFields";
import { DEFAULT_ADDRESS_COUNTRY } from "../../utils/addressLocationOptions";
import { StyledAddressFieldGrid } from "../../components/address-default/style";
import { useAddressLocationData } from "../../hooks/useAddressLocationData";
import { StyledBoxFlex } from "../../components/modals/success-virtual-locker/style";
import { StyledTypography } from "../../components/modals/manual-split-calculation/style";
import { Add, Delete } from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { VIRTUAL_LOCKER_ADD_ITEM, VIRTUAL_LOCKER_GET_ITEM_BY_ID, VIRTUAL_LOCKER_UPDATE_ITEM } from "../../actions/VirtualLockerAction";
import { CUSTOMER_ORDER_LIST } from "../../actions/OrderAction";
import {
  ADD_LOCKER_ITEMS_BLOCKED_MESSAGE,
  getLockerItemBlockReason,
} from "../../utils/orderCreateEligibility";
import { minimumTodayDateValidation, isLockerItemEditDisabled } from "../../utils/common";
import CustomDropdown from "../../components/common/common-dropdown/CustomDropdown";
import CustomInput from "../../components/common/input/CustomInput";
import CustomButton from "../../components/common/button/CustomButton";
import { countryRules, getPhoneValidation } from "../../utils/countryRules";
import { API } from "../../api";
import toast from "react-hot-toast";

const PS_FIELD_GRID = { lg: 6, md: 6, sm: 12, xs: 12 };
const PICKUP_LOCATION_GRID = { lg: 4, md: 4, sm: 12, xs: 12 };
const PU_FIELD_GRID = PICKUP_LOCATION_GRID;

const selfDropItems = [
  {
    order_type: "self_drop",
    preferred_date: "",
    preferred_time: "",
    dropper_name: "",
    contact_mobile: "",
    products: [{ product_name: "", quantity: "", weight: "", product_description: "" }]
  }
]
const personalShopperItems = [
  {
    order_type: "personal_shopper",
    shop_date: "",
    shop_time: "",
    shop_name: "",
    shop_location: "",
    contact_mobile: "",
    shop_address: "",
    shop_country: DEFAULT_ADDRESS_COUNTRY,
    shop_state: "",
    shop_city: "",
    payment_status: "",
    invoice_of_product: "",
    products: [{ product_name: "", quantity: "", approx_rate: "", product_description: "" }]
  }
]
const shoppingItems = [
  {
    order_type: "online_shopping",
    products: [
      {
        product_name: "",
        quantity: "",
        source_name: "",
        product_url: "",
        order_id: "",
        order_proof_url: "",
        estimated_delivery: "",
      },
    ],
  },
];

const pickupItems = [
  {
    order_type: "pick_up",
    contact_person: "",
    contact_mobile: "",
    pickup_address: "",
    pickup_country: DEFAULT_ADDRESS_COUNTRY,
    pickup_state: "",
    pickup_city: "",
    pickup_landmark: "",
    pickup_date: "",
    pickup_time: "",
    products: [{ product_name: "", quantity: "", weight: "", product_description: "" }]
  }
]

export default function VirtualLockerItemV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get("id");
  const [active, setActive] = React.useState("self_drop");
  const [currentCountryCode, setCurrentCountryCode] = React.useState("+91");
  const [initialData, setInitialData] = React.useState(null);
  const [proofUploadKey, setProofUploadKey] = React.useState(null);
  const [invoiceUploading, setInvoiceUploading] = React.useState(false);

  const handleInvoiceUpload = React.useCallback(async (event, ind, setFieldValue) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setInvoiceUploading(true);
    try {
      const res = await API.uploadImage(file);
      const url = res?.data?.url;
      if (url) {
        setFieldValue(`items[${ind}].invoice_of_product`, url);
        toast.success("Invoice uploaded");
      }
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Upload failed.");
    } finally {
      setInvoiceUploading(false);
      event.target.value = "";
    }
  }, []);

  const {
    countries,
    loadingCountries,
    getStatesForCountry,
    getCitiesForCountryState,
  } = useAddressLocationData();

  const lockerGetById = useSelector((state) => state?.virtual?.lockerGetById);
  const lockerGetDetails = useSelector((state) => state?.address?.virtualAddress);
  const isLockerSubmitting = useSelector((state) => state?.virtual?.isLoading);
  const submitLockRef = React.useRef(false);
  const { orderSummary, hasFetchedCustomerOrderList, recordList } = useSelector(
    (state) => state?.order ?? {}
  );

  React.useEffect(() => {
    if (!isLockerSubmitting) {
      submitLockRef.current = false;
    }
  }, [isLockerSubmitting]);

  const addItemsBlockReason = getLockerItemBlockReason(
    lockerGetDetails,
    orderSummary,
    hasFetchedCustomerOrderList,
    recordList
  );
  const canAddItems = !addItemsBlockReason;

  React.useEffect(() => {
    dispatch({ type: CUSTOMER_ORDER_LIST, payload: { page: 1, limit: 10 } });
  }, [dispatch]);

  React.useEffect(() => {
    if (itemId || !hasFetchedCustomerOrderList) return;
    if (!canAddItems) {
      toast.error(addItemsBlockReason || ADD_LOCKER_ITEMS_BLOCKED_MESSAGE);
      navigate("/dashboard/locker-v2", { replace: true });
    }
  }, [
    itemId,
    hasFetchedCustomerOrderList,
    canAddItems,
    addItemsBlockReason,
    navigate,
  ]);

  const handleOrderProofUpload = React.useCallback(
    async (event, ind, prodIndex, setFieldValue) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const key = `${ind}-${prodIndex}`;
      setProofUploadKey(key);
      try {
        const res = await API.uploadImage(file);
        const url = res?.data?.url;
        if (url) {
          setFieldValue(
            `items[${ind}].products[${prodIndex}].order_proof_url`,
            url
          );
          toast.success("Order file uploaded");
        }
      } catch (err) {
        toast.error(
          err?.data?.message || err?.message || "Upload failed. Try an image or smaller PDF."
        );
      } finally {
        setProofUploadKey(null);
        event.target.value = "";
      }
    },
    []
  );

  React.useEffect(() => {
    if (itemId) {
      dispatch({ type: VIRTUAL_LOCKER_GET_ITEM_BY_ID, payload: { id: itemId } });
    }
  }, [itemId, dispatch]);

  React.useEffect(() => {
    if (itemId && lockerGetById) {
      const item = Array.isArray(lockerGetById)
        ? lockerGetById.find((i) => String(i.id) === String(itemId))
        : lockerGetById;
      if (item) {
        setInitialData(item);
        setActive(item.order_type || "self_drop");
      }
    }
  }, [itemId, lockerGetById]);

  React.useEffect(() => {
    if (!itemId || !initialData?.status) return;
    if (isLockerItemEditDisabled(initialData.status)) {
      const status = String(initialData.status).toLowerCase();
      const message =
        status === "on_hold" || status === "shopping_in_progress"
          ? "This item cannot be edited while it is On Hold or In Progress."
          : "This item cannot be edited while pictures are awaiting your review.";
      toast.error(message);
      navigate("/dashboard/locker-v2", { replace: true });
    }
  }, [itemId, initialData?.status, navigate]);

  const getValidationSchema = React.useCallback((values = {}) => {
    const fieldType = values.fieldType;
    switch (fieldType) {
      case "self_drop":
        return Yup.object({
          items: Yup.array()
            .of(
              Yup.object({
                order_type: Yup.string()
                  .required("Order type is required")
                  .oneOf(["self_drop"], "Invalid order type"),
                preferred_date: Yup.date()
                  .typeError("Preferred date is required")
                  .required("Preferred date is required"),
                preferred_time: Yup.string()
                  .required("Preferred time is required"),
                dropper_name: Yup.string()
                  .required("Dropper name is required"),
                contact_mobile: Yup.string()
                  .notRequired()
                  .test(getPhoneValidation(currentCountryCode)),
                products: Yup.array()
                  .of(
                    Yup.object({
                      product_name: Yup.string().required("Product name is required"),
                      quantity: Yup.number()
                        .typeError("Quantity must be a number")
                        .positive("Quantity must be greater than zero")
                        .integer("Quantity must be an integer")
                        .notRequired(),
                      weight: Yup.number()
                        .typeError("Weight must be a number")
                        .positive("Weight must be greater than zero")
                        .required("Weight is required"),
                      product_description: Yup.string().notRequired(),
                    })
                  )
                  .min(1, "At least one product is required")
                  .required("Products are required"),
              })
            )
            .min(1, "At least one self drop is required")
            .required("Self drop data is required"),
        });
      case "personal_shopper":
        return Yup.object({
          items: Yup.array()
            .of(
              Yup.object({
                order_type: Yup.string().oneOf(["personal_shopper"]).required(),
                shop_date: Yup.date().required("Shop date is required"),
                shop_time: Yup.string().required("Shop time is required"),
                shop_name: Yup.string().required("Shop name is required"),
                shop_location: Yup.string().required("Shop location is required"),
                contact_mobile: Yup.string()
                  .notRequired()
                  .test(getPhoneValidation(currentCountryCode)),
                shop_address: Yup.string().required("Shop address is required"),
                shop_country: Yup.string().required("Shop country is required"),
                shop_state: Yup.string().required("Shop state is required"),
                shop_city: Yup.string().required("Shop city is required"),
                payment_status: Yup.string().notRequired(),
                invoice_of_product: Yup.string().when("payment_status", {
                  is: (val) => val === "paid" || val === "partial_paid",
                  then: (s) => s.required("Payment proof is required"),
                  otherwise: (s) => s.notRequired(),
                }),
                products: Yup.array()
                  .of(
                    Yup.object({
                      product_name: Yup.string().required("Product name is required"),
                      quantity: Yup.number()
                        .typeError("Quantity must be a number")
                        .positive("Quantity must be greater than 0")
                        .required("Quantity is required"),
                      approx_rate: Yup.number()
                        .typeError("Rate must be a number")
                        .positive("Rate must be greater than 0")
                        .required("Approx. rate is required"),
                      product_description: Yup.string().notRequired(),
                    })
                  )
                  .min(1, "At least one product is required")
              })
            )
            .min(1, "At least one personal shopper entry is required")
        });
      case "online_shopping":
        return Yup.object().shape({
          items: Yup.array()
            .of(
              Yup.object().shape({
                products: Yup.array()
                  .of(
                    Yup.object().shape({
                      product_name: Yup.string()
                        .trim()
                        .required("Product name is required"),
                      quantity: Yup.number()
                        .typeError("Quantity must be a number")
                        .positive("Quantity must be greater than zero")
                        .integer("Quantity must be an integer")
                        .required("Quantity is required"),
                      source_name: Yup.string().optional(),
                      product_url: Yup.string().optional(),
                      order_id: Yup.string()
                        .trim()
                        .required("Online Order Number is required"),
                      order_proof_url: Yup.string().notRequired(),
                      estimated_delivery: Yup.date()
                        .typeError("Invalid date format")
                        .required("Estimated delivery date is required"),
                    })
                  )
                  .min(1, "At least one product is required")
                  .required("Products are required"),
              })
            )
            .min(1, "At least one shopping item is required"),
        });
      case "pick_up":
        return Yup.object({
          items: Yup.array()
            .of(
              Yup.object({
                order_type: Yup.string().oneOf(["pick_up"]).required(),
                contact_person: Yup.string().required("Contact person is required"),
                contact_mobile: Yup.string()
                  .notRequired()
                  .test(getPhoneValidation(currentCountryCode)),
                pickup_address: Yup.string().required("Pickup address is required"),
                pickup_country: Yup.string().required("Pickup country is required"),
                pickup_state: Yup.string().required("Pickup state is required"),
                pickup_city: Yup.string().required("Pickup city is required"),
                pickup_landmark: Yup.string().required("Pickup landmark is required"),
                pickup_date: Yup.string().required("Pickup date is required"),
                pickup_time: Yup.string().required("Pickup time is required"),
                products: Yup.array()
                .of(
                  Yup.object().shape({
                    product_name: Yup.string()
                      .required("Product name is required"),

                    quantity: Yup.number()
                      .typeError("Quantity must be a number")
                      .positive("Quantity must be greater than 0")
                      .integer("Quantity must be an integer")
                      .notRequired(),

                    weight: Yup.number()
                      .typeError("Weight must be a number")
                      .required("Weight is required")
                      .positive("Weight must be greater than 0"),

                    product_description: Yup.string().notRequired(),
                  })
                )
                .min(1, "At least one product is required"),
              })
            )
            .min(1, "At least one personal shopper entry is required")
        });
      default:
        return Yup.object().shape({});
    }
  }, [currentCountryCode]);
  const [validationSchema, setValidationSchema] = React.useState(
    () => getValidationSchema({ fieldType: "self_drop" }) // default initial schema
  );

  React.useEffect(() => {
    setValidationSchema(getValidationSchema({ fieldType: active }));
  }, [active, currentCountryCode, getValidationSchema]);

  const flattenOrders = (data) =>
  data.flatMap(order => {
    const { products, ...orderFields } = order;
    return (products || []).map(product => {
      const flattenedProduct = {
        ...orderFields,
        ...product,
        weight: product.weight !== undefined && product.weight !== "" ? String(product.weight) : product.weight,
        contact_mobile: orderFields.contact_mobile !== undefined ? String(orderFields.contact_mobile) : orderFields.contact_mobile,
      };

      if (orderFields.order_type !== "self_drop") {
        flattenedProduct.quantity =
          product.quantity !== undefined && product.quantity !== null
            ? String(product.quantity)
            : product.quantity;
      } else {
        delete flattenedProduct.quantity;
      }

      return flattenedProduct;
    });
  });

  const toDate = (v) => (v ? v.split("T")[0] : "");

  const getInitialValues = () => {
    if (!initialData) return { fieldType: "self_drop", items: selfDropItems };
    const t = initialData.order_type;
    const prod = {
      product_name: initialData.product_name || "",
      product_description: initialData.product_description || "",
    };
    switch (t) {
      case "personal_shopper":
        return {
          fieldType: t,
          items: [{
            order_type: t,
            shop_name: initialData.shop_name || "",
            shop_location: initialData.shop_location || "",
            contact_mobile:
              initialData.contact_mobile != null
                ? String(initialData.contact_mobile)
                : "",
            shop_address: initialData.shop_address || "",
            shop_country: initialData.shop_country || DEFAULT_ADDRESS_COUNTRY,
            shop_state: initialData.shop_state || "",
            shop_city: initialData.shop_city || "",
            payment_status: initialData.payment_status || "",
            invoice_of_product: initialData.invoice_of_product || "",
            shop_date: toDate(initialData.shop_date || initialData.date),
            shop_time: initialData.shop_time || initialData.time || "",
            products: [{
              ...prod,
              quantity: initialData.quantity != null ? String(initialData.quantity) : "",
              approx_rate: initialData.approx_rate != null ? String(initialData.approx_rate) : "",
            }],
          }],
        };
      case "online_shopping":
        return {
          fieldType: t,
          items: [
            {
              order_type: t,
              products: [
                {
                  ...prod,
                  quantity:
                    initialData.quantity != null
                      ? String(initialData.quantity)
                      : "",
                  source_name: initialData.source_name || "",
                  product_url: initialData.product_url || "",
                  order_id: String(initialData.additional_comments || "").trim(),
                  order_proof_url: String(
                    initialData.invoice_of_product || ""
                  ).trim(),
                  estimated_delivery: toDate(initialData.estimated_delivery),
                },
              ],
            },
          ],
        };
      case "pick_up":
        return {
          fieldType: t,
          items: [{
            order_type: t,
            contact_person: initialData.contact_person || "",
            contact_mobile: initialData.contact_mobile || "",
            pickup_address: initialData.pickup_address || "",
            pickup_country:
              initialData.pickup_country ||
              initialData.country ||
              DEFAULT_ADDRESS_COUNTRY,
            pickup_state: initialData.pickup_state || initialData.state || "",
            pickup_city: initialData.pickup_city || initialData.city || "",
            pickup_landmark: initialData.pickup_landmark || "",
            pickup_date: toDate(initialData.pickup_date || initialData.date),
            pickup_time: initialData.pickup_time || initialData.time || "",
            products: [{
              ...prod,
              quantity: initialData.quantity != null ? String(initialData.quantity) : "",
              weight: initialData.weight ? String(initialData.weight) : "",
            }],
          }],
        };
      default:
        return {
          fieldType: "self_drop",
          items: [{
            order_type: "self_drop",
            preferred_date: toDate(initialData.preferred_date || initialData.pickup_date || initialData.date),
            preferred_time: initialData.preferred_time || initialData.pickup_time || initialData.time || "",
            dropper_name: initialData.dropper_name || "",
            contact_mobile:
              initialData.contact_mobile != null
                ? String(initialData.contact_mobile)
                : "",
            products: [{ ...prod, weight: initialData.weight ? String(initialData.weight) : "" }],
          }],
        };
    }
  };

  return (
    <StyledBoxV2>
      <Button
        size="small"
        startIcon={<ChevronLeftIcon />}
        onClick={() => navigate(-1)}
        sx={{ alignSelf: "flex-start", color: theme.palette.custom.primary }}
      >
        Back
      </Button>
      <StyledBoxCard>
        <StyledBoxFlex sx={{ width: "100%" }}>
          <StyledTypography>Create Items</StyledTypography>
        </StyledBoxFlex>
      </StyledBoxCard>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(values) => {
          if (submitLockRef.current || isLockerSubmitting) return;
          submitLockRef.current = true;

          const items = flattenOrders(values?.items).map((row) => {
            if (row.order_type !== "online_shopping") return row;
            const { order_id, order_proof_url, ...rest } = row;
            return {
              ...rest,
              additional_comments: order_id || rest.additional_comments,
              invoice_of_product: order_proof_url || rest.invoice_of_product,
            };
          });
          if (itemId) {
            const raw = { ...items[0] };
            // remap form field names back to API field names
            if (raw.preferred_date) { raw.pickup_date = raw.preferred_date; delete raw.preferred_date; }
            if (raw.preferred_time) {
              raw.pickup_time = raw.preferred_time;
              delete raw.preferred_time;
            }
            if (raw.order_type === "pick_up") {
              if (!raw.pickup_time && raw.time) {
                raw.pickup_time = raw.time;
              }
            }
            delete raw.time;
            // shop_date and shop_time are already correctly named, no remapping needed
            // pickup_date stays as pickup_date for both self_drop and pick_up
            dispatch({
              type: VIRTUAL_LOCKER_UPDATE_ITEM,
              payload: {
                id: itemId,
                ...raw,
                onSuccess: () => { window.history.back(); },
              },
            });
          } else {
            if (!canAddItems) {
              submitLockRef.current = false;
              toast.error(addItemsBlockReason || ADD_LOCKER_ITEMS_BLOCKED_MESSAGE);
              return;
            }
            dispatch({
              type: VIRTUAL_LOCKER_ADD_ITEM,
              payload: {
                items: items,
                onSuccess: () => { window.history.back(); },
              },
            });
          }
        }}
      >
        {({
          errors,
          handleChange,
          handleBlur,
          values,
          touched,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form>
            <Box>
              <StyledMain>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    width: "100%",
                    overflowX: "auto",
                    flexWrap: "nowrap",
                    pb: { xs: 1, md: 0 },
                    pr: 0.5,
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { width: 0, height: 0 },
                  }}
                >
                  <StyledGrid
                    isactive={active === "self_drop"}
                    onClick={(event) => {
                      if (!itemId) {
                        setActive("self_drop");
                        setFieldValue("items", selfDropItems);
                        setFieldValue("fieldType", "self_drop");
                        event?.currentTarget?.scrollIntoView?.({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      } else if (active === "self_drop") {
                        event?.currentTarget?.scrollIntoView?.({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      }
                    }}
                    sx={{ cursor: (itemId && active !== "self_drop") ? "not-allowed" : "pointer", opacity: (itemId && active !== "self_drop") ? 0.4 : 1 }}
                  >
                    Self Drop
                  </StyledGrid>
                  <StyledGrid
                    isactive={active === "personal_shopper"}
                    onClick={(event) => {
                      if (!itemId) {
                        setActive("personal_shopper");
                        setFieldValue("items", personalShopperItems);
                        setFieldValue("fieldType", "personal_shopper");
                        event?.currentTarget?.scrollIntoView?.({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      } else if (active === "personal_shopper") {
                        event?.currentTarget?.scrollIntoView?.({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      }
                    }}
                    sx={{ cursor: (itemId && active !== "personal_shopper") ? "not-allowed" : "pointer", opacity: (itemId && active !== "personal_shopper") ? 0.4 : 1 }}
                  >
                    Personal Shopper
                  </StyledGrid>
                  <StyledGrid
                    isactive={active === "online_shopping"}
                    onClick={(event) => {
                      if (!itemId) {
                        setActive("online_shopping");
                        setFieldValue("items", shoppingItems);
                        setFieldValue("fieldType", "online_shopping");
                        event?.currentTarget?.scrollIntoView?.({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      } else if (active === "online_shopping") {
                        event?.currentTarget?.scrollIntoView?.({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      }
                    }}
                    sx={{ cursor: (itemId && active !== "online_shopping") ? "not-allowed" : "pointer", opacity: (itemId && active !== "online_shopping") ? 0.4 : 1 }}
                  >
                    Online Shopping
                  </StyledGrid>
                  <StyledGrid
                    isactive={active === "pick_up"}
                    onClick={(event) => {
                      if (!itemId) {
                        setActive("pick_up");
                        setFieldValue("items", pickupItems);
                        setFieldValue("fieldType", "pick_up");
                        event?.currentTarget?.scrollIntoView?.({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      } else if (active === "pick_up") {
                        event?.currentTarget?.scrollIntoView?.({
                          behavior: "smooth",
                          block: "nearest",
                          inline: "center",
                        });
                      }
                    }}
                    sx={{ cursor: (itemId && active !== "pick_up") ? "not-allowed" : "pointer", opacity: (itemId && active !== "pick_up") ? 0.4 : 1 }}
                  >
                    Pickup
                  </StyledGrid>
                </Box>
              </StyledMain>
            </Box>
            <FieldArray name="items">
              {({ push, remove }) => (
                <StyledMain>
                  {values?.items?.map((data, ind) => {
                    const dropBase = `items[${ind}]`;
                    // const dropErrors = getIn(errors, dropBase) || {};
                    // const dropTouched = getIn(touched, dropBase) || {};
                    const products = getIn(values, `${dropBase}.products`) || [];
                    return (
                      <React.Fragment key={ind} >
                        {/* <Box > */}
                            <Box>
                              {active === "self_drop" && (
                                <StyleBoxContainer>
                                  <Grid container columnSpacing={2}>
                                    <Grid
                                      size={{
                                        lg: 3,
                                        md: 6,
                                        sm: 6,
                                        xs: 12,
                                      }}
                                    >
                                      <CustomInput
                                        formGrid
                                        color={
                                          theme.palette.custom
                                            .productTextColor
                                        }
                                        fontSize={
                                          theme.typography.regular.fontSize
                                        }
                                        height="10px"
                                        required
                                        type="date"
                                        customLabel="Preferred Drop Date"
                                        min={minimumTodayDateValidation()}
                                        name={`items[${ind}].preferred_date`}
                                        value={data.preferred_date}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched.items?.[ind]
                                            ?.preferred_date &&
                                          Boolean(
                                            errors.items?.[ind]
                                              ?.preferred_date
                                          )
                                        }
                                        helperText={
                                          touched.items?.[ind]
                                            ?.preferred_date &&
                                          errors.items?.[ind]
                                            ?.preferred_date
                                        }
                                      />
                                    </Grid>
                                    <Grid
                                      size={{
                                        lg: 3,
                                        md: 6,
                                        sm: 6,
                                        xs: 12,
                                      }}
                                    >
                                      <CustomDropdown
                                        formGrid
                                        required
                                        options={selfDropTimeOptions}
                                        color={
                                          theme.palette.custom
                                            .productTextColor
                                        }
                                        fontSize={
                                          theme.typography.regular.fontSize
                                        }
                                        height="10px"
                                        customLabel="Time"
                                        name={`items[${ind}].preferred_time`}
                                        value={data.preferred_time}
                                        onBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        error={
                                          touched.items?.[ind]
                                            ?.preferred_time &&
                                          Boolean(
                                            errors.items?.[ind]
                                              ?.preferred_time
                                          )
                                        }
                                        helperText={
                                          touched.items?.[ind]
                                            ?.preferred_time &&
                                          errors.items?.[ind]
                                            ?.preferred_time
                                        }
                                      />
                                    </Grid>
                                    <Grid
                                      size={{
                                        lg: 3,
                                        md: 6,
                                        sm: ind !== 0 ? 5 : 6,
                                        xs: 12,
                                      }}
                                    >
                                      <CustomInput
                                        formGrid
                                        color={
                                          theme.palette.custom
                                            .productTextColor
                                        }
                                        fontSize={
                                          theme.typography.regular.fontSize
                                        }
                                        height="10px"
                                        required
                                        customLabel="Dropper Name"
                                        placeholder="Dropper Name"
                                        name={`items[${ind}].dropper_name`}
                                        value={data.dropper_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched?.items?.[ind]
                                            ?.dropper_name &&
                                          Boolean(
                                            errors?.items?.[ind]?.dropper_name
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.dropper_name &&
                                          errors?.items?.[ind]?.dropper_name
                                        }
                                      />
                                    </Grid>
                                    <Grid
                                      size={{
                                        lg: ind !== 0 ? 2 : 3,
                                        md: 6,
                                        sm: ind !== 0 ? 6 : 6,
                                        xs: 12,
                                      }}
                                    >
                                      <CustomInput
                                        formGrid
                                        color={
                                          theme.palette.custom
                                            .productTextColor
                                        }
                                        fontSize={
                                          theme.typography.regular.fontSize
                                        }
                                        height="10px"
                                        customLabel="Dropper Contact Number"
                                        placeholder="Dropper Contact Number"
                                        name={`items[${ind}].contact_mobile`}
                                        value={data.contact_mobile}
                                        onChange={(e) => {
                                          const rules =
                                            countryRules[currentCountryCode] ||
                                            countryRules.default;
                                          const val = e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, rules.max);
                                          setFieldValue(
                                            `items[${ind}].contact_mobile`,
                                            val
                                          );
                                        }}
                                        onBlur={handleBlur}
                                        isPhoneNumber
                                        currentCountryCode={currentCountryCode}
                                        setCurrentCountryCode={
                                          setCurrentCountryCode
                                        }
                                        error={
                                          touched?.items?.[ind]
                                            ?.contact_mobile &&
                                          Boolean(
                                            errors?.items?.[ind]?.contact_mobile
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.contact_mobile &&
                                          errors?.items?.[ind]?.contact_mobile
                                        }
                                      />
                                    </Grid>
                                    {(ind !==0 ) && (
                                      <Grid
                                        size={{
                                          lg: 1,
                                          md: 1,
                                          sm: 1,
                                          xs: 12,
                                        }}
                                      >
                                          <IconButtonStyle
                                            color="error"
                                            onClick={() => remove(ind)}
                                          >
                                            <Delete />
                                          </IconButtonStyle>
                                      </Grid>
                                    )}
                                    {/* Products FieldArray */}
                                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                      <StyleTable sx={{ minWidth: 800 }}>
                                        <StyleTableHead>
                                          <TableRow>
                                            <TableCell width="35%">Product Name</TableCell>
                                            <TableCell width="20%">Weight</TableCell>
                                            <TableCell width="35%">Product Comment</TableCell>
                                            <StyleTableActionHeaderCell>
                                              Action
                                            </StyleTableActionHeaderCell>
                                          </TableRow>
                                        </StyleTableHead>
                                      <TableBody>
                                        <FieldArray name={`items[${ind}].products`}>
                                          {({ push, remove }) => (
                                            <>
                                              {products?.map((prod, prodIndex) => {
                                              // const prodErrors =
                                              //   dropErrors.products?.[prodIndex] || {};
                                              // const prodTouched =
                                              //   dropTouched.products?.[prodIndex] || {};
                                              return (
                                                <TableRow key={prodIndex}>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      color={
                                                        theme.palette.custom
                                                          .productTextColor
                                                      }
                                                      fontSize={
                                                        theme.typography.regular.fontSize
                                                      }
                                                      height="10px"
                                                      placeholder="Product Name"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].product_name`}
                                                      value={prod.product_name}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_name &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.product_name
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_name &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.product_name
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  {/* <StyleTableCell>
                                                    <CustomDropdown
                                                      options={arrOptionNumber}
                                                      color={
                                                        theme.palette.custom
                                                          .productTextColor
                                                      }
                                                      fontSize={
                                                        theme.typography.regular.fontSize
                                                      }
                                                      height="44px"
                                                      onBlur={handleBlur}
                                                      setFieldValue={setFieldValue}
                                                      name={`items[${ind}].products[${prodIndex}].quantity`}
                                                      value={prod.quantity}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.quantity &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.quantity
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.quantity &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.quantity
                                                      }
                                                    />
                                                  </StyleTableCell> */}
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      color={
                                                        theme.palette.custom
                                                          .productTextColor
                                                      }
                                                      endAdornment={
                                                        <InputAdornment position="end">
                                                          Kg
                                                        </InputAdornment>
                                                      }
                                                      fontSize={
                                                        theme.typography.regular.fontSize
                                                      }
                                                      height="10px"
                                                      type="number"
                                                      placeholder="Weight"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].weight`}
                                                      value={prod.weight}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.weight &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.weight
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.weight &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.weight
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      color={
                                                        theme.palette.custom
                                                          .productTextColor
                                                      }
                                                      fontSize={
                                                        theme.typography.regular.fontSize
                                                      }
                                                      height="10px"
                                                      type="text"
                                                      placeholder="Comment"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].product_description`}
                                                      value={prod.product_description}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_description &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.product_description
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_description &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.product_description
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableActionCell>
                                                    <StyleTableActionButtonWrap>
                                                      <StyleTableIconButton
                                                        color="error"
                                                        disabled={!!itemId}
                                                        onClick={() => !itemId && remove(prodIndex)}
                                                      >
                                                        <Delete />
                                                      </StyleTableIconButton>
                                                    </StyleTableActionButtonWrap>
                                                  </StyleTableActionCell>
                                                </TableRow>
                                              );
                                              })}
                                                <TableRow>
                                                  <TableCell colSpan={4} sx={{ borderBottom: 'none' }}>
                                                    <Button
                                                      size="small"
                                                      startIcon={<Add />}
                                                      onClick={() =>
                                                        push({
                                                          product_name: "",
                                                          quantity: "",
                                                          weight: "",
                                                          product_description: "",
                                                        })
                                                      }
                                                     style={{display: itemId ? "none" : undefined}}>
                                                      Add another product item
                                                    </Button>
                                                  </TableCell>
                                                </TableRow>
                                            </>
                                          )}
                                        </FieldArray>
                                      </TableBody>
                                    </StyleTable>
                                    </Box>
                                  </Grid>
                                </StyleBoxContainer>
                              )}
                              {active === "personal_shopper" && (
                                <StyleBoxContainer>
                                  <StyledPersonalShopperFieldGrid
                                    container
                                    columnSpacing={1}
                                    rowSpacing={1}
                                  >
                                    <StyledAddressFieldGrid size={PS_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        required
                                        customLabel="Shop Name"
                                        placeholder="Shop Name"
                                        name={`items[${ind}].shop_name`}
                                        value={data.shop_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched?.items?.[ind]
                                            ?.shop_name &&
                                          Boolean(
                                            errors?.items?.[ind]?.shop_name
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.shop_name &&
                                          errors?.items?.[ind]?.shop_name
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PS_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        required
                                        customLabel="Shop Location"
                                        placeholder="Shop Location"
                                        name={`items[${ind}].shop_location`}
                                        value={data.shop_location}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched?.items?.[ind]
                                            ?.shop_location &&
                                          Boolean(
                                            errors?.items?.[ind]?.shop_location
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.shop_location &&
                                          errors?.items?.[ind]?.shop_location
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PS_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        customLabel="Shop Contact Number"
                                        placeholder="Shop Contact Number"
                                        name={`items[${ind}].contact_mobile`}
                                        value={data.contact_mobile}
                                        onChange={(e) => {
                                          const rules =
                                            countryRules[currentCountryCode] ||
                                            countryRules.default;
                                          const val = e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, rules.max);
                                          setFieldValue(
                                            `items[${ind}].contact_mobile`,
                                            val
                                          );
                                        }}
                                        onBlur={handleBlur}
                                        isPhoneNumber
                                        currentCountryCode={currentCountryCode}
                                        setCurrentCountryCode={
                                          setCurrentCountryCode
                                        }
                                        error={
                                          touched?.items?.[ind]
                                            ?.contact_mobile &&
                                          Boolean(
                                            errors?.items?.[ind]?.contact_mobile
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.contact_mobile &&
                                          errors?.items?.[ind]?.contact_mobile
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PS_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        required
                                        type="date"
                                        customLabel="Preferred Shopping Date"
                                        min={minimumTodayDateValidation()}
                                        name={`items[${ind}].shop_date`}
                                        value={data.shop_date}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched.items?.[ind]
                                            ?.shop_date &&
                                          Boolean(
                                            errors.items?.[ind]
                                              ?.shop_date
                                          )
                                        }
                                        helperText={
                                          touched.items?.[ind]
                                            ?.shop_date &&
                                          errors.items?.[ind]
                                            ?.shop_date
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PS_FIELD_GRID}>
                                      <CustomDropdown
                                        isprofile
                                        formGrid
                                        required
                                        options={selfDropTimeOptions}
                                        color={
                                          theme.palette.custom
                                            .productTextColor
                                        }
                                        fontSize={
                                          theme.typography.regular.fontSize
                                        }
                                        customLabel="Time"
                                        placeholder="Select time"
                                        name={`items[${ind}].shop_time`}
                                        value={data.shop_time}
                                        onBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        error={
                                          touched.items?.[ind]
                                            ?.shop_time &&
                                          Boolean(
                                            errors.items?.[ind]
                                              ?.shop_time
                                          )
                                        }
                                        helperText={
                                          touched.items?.[ind]
                                            ?.shop_time &&
                                          errors.items?.[ind]
                                            ?.shop_time
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <CountryStateFields
                                      fieldPrefix={`items[${ind}]`}
                                      hideCountry
                                      countryKey="shop_country"
                                      stateKey="shop_state"
                                      cityKey="shop_city"
                                      countryLabel="Shop Country"
                                      stateLabel="Shop State"
                                      cityLabel="Shop City"
                                      formGrid
                                      muiGridRow
                                      gridCellSize={PS_FIELD_GRID}
                                      countries={countries}
                                      loadingCountries={loadingCountries}
                                      getStatesForCountry={getStatesForCountry}
                                      getCitiesForCountryState={getCitiesForCountryState}
                                      values={values}
                                      errors={errors}
                                      touched={touched}
                                      setFieldValue={setFieldValue}
                                      handleBlur={handleBlur}
                                    />
                                    <StyledAddressFieldGrid size={PS_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        required
                                        customLabel="Shop Address"
                                        placeholder="Shop Address"
                                        name={`items[${ind}].shop_address`}
                                        value={data.shop_address}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched?.items?.[ind]
                                            ?.shop_address &&
                                          Boolean(
                                            errors?.items?.[ind]?.shop_address
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.shop_address &&
                                          errors?.items?.[ind]?.shop_address
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PS_FIELD_GRID}>
                                      <CustomDropdown
                                        isprofile
                                        formGrid
                                        options={paymentStatusOptions}
                                        color={
                                          theme.palette.custom
                                            .productTextColor
                                        }
                                        fontSize={
                                          theme.typography.regular.fontSize
                                        }
                                        customLabel="Did you paid for the personal shopping item?"
                                        placeholder="Select payment status"
                                        name={`items[${ind}].payment_status`}
                                        value={data.payment_status}
                                        onBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        error={
                                          touched.items?.[ind]
                                            ?.payment_status &&
                                          Boolean(
                                            errors.items?.[ind]
                                              ?.payment_status
                                          )
                                        }
                                        helperText={
                                          touched.items?.[ind]
                                            ?.payment_status &&
                                          errors.items?.[ind]
                                            ?.payment_status
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    {(data.payment_status === "paid" || data.payment_status === "partial_paid") && (
                                      <StyledAddressFieldGrid size={PS_FIELD_GRID}>
                                        <StyledUploadWrapper>
                                          <StyledUploadLabel>
                                            Invoice / Payment Proof <span>*</span>
                                          </StyledUploadLabel>
                                          <StyledUploadField hasfile={data.invoice_of_product ? 1 : 0} haserror={touched.items?.[ind]?.invoice_of_product && errors.items?.[ind]?.invoice_of_product ? 1 : 0}>
                                            <input
                                              type="file"
                                              accept="image/*,.pdf,application/pdf"
                                              id={`invoice-upload-${ind}`}
                                              style={{ display: "none" }}
                                              onChange={(e) => handleInvoiceUpload(e, ind, setFieldValue)}
                                            />
                                            <StyledUploadFileLabel htmlFor={`invoice-upload-${ind}`}>
                                              <StyledUploadButton
                                                component="span"
                                                variant="outlined"
                                                fullWidth
                                                hasfile={data.invoice_of_product ? 1 : 0}
                                                startIcon={invoiceUploading ? null : <CloudUploadOutlinedIcon fontSize="small" />}
                                                disabled={invoiceUploading}
                                              >
                                                {invoiceUploading ? <CircularProgress size={16} /> : (data.invoice_of_product ? "Invoice uploaded ✓" : "Upload Invoice / Payment Proof")}
                                              </StyledUploadButton>
                                            </StyledUploadFileLabel>
                                          </StyledUploadField>
                                          {touched.items?.[ind]?.invoice_of_product && errors.items?.[ind]?.invoice_of_product && (
                                            <StyledUploadErrorText>
                                              {errors.items?.[ind]?.invoice_of_product}
                                            </StyledUploadErrorText>
                                          )}
                                        </StyledUploadWrapper>
                                      </StyledAddressFieldGrid>
                                    )}
                                    {(ind !==0 ) && (
                                      <Grid
                                        size={{
                                          lg: 2,
                                          md: 2,
                                          sm: 2,
                                          xs: 12,
                                        }}
                                      >
                                          <IconButtonStyle
                                            color="error"
                                            onClick={() => remove(ind)}
                                          >
                                            <Delete />
                                          </IconButtonStyle>
                                      </Grid>
                                    )}
                                    {/* Products FieldArray */}
                                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                      <StyleTable sx={{ minWidth: 800 }}>
                                        <StyleTableHead>
                                          <TableRow>
                                            <TableCell width="30%">Product Name</TableCell>
                                            <TableCell width="30%">Product Description</TableCell>
                                            <TableCell width="15%">Quantity</TableCell>
                                            <TableCell width="15%">Approx Rate</TableCell>
                                            <StyleTableActionHeaderCell>
                                              Action
                                            </StyleTableActionHeaderCell>
                                          </TableRow>
                                        </StyleTableHead>
                                      <TableBody>
                                        <FieldArray name={`items[${ind}].products`}>
                                          {({ push, remove }) => (
                                            <>
                                              {products?.map((prod, prodIndex) => {
                                              // const prodErrors =
                                              //   dropErrors.products?.[prodIndex] || {};
                                              // const prodTouched =
                                              //   dropTouched.products?.[prodIndex] || {};
                                              return (
                                                <TableRow key={prodIndex}>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      placeholder="Product Name"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].product_name`}
                                                      value={prod.product_name}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_name &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.product_name
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_name &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.product_name
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      type="text"
                                                      placeholder="Product Description"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].product_description`}
                                                      value={prod.product_description}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_description &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.product_description
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_description &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.product_description
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      type="number"
                                                      placeholder="Qty"
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].quantity`}
                                                      value={prod.quantity}
                                                      onChange={(e) => setFieldValue(`items[${ind}].products[${prodIndex}].quantity`, e.target.value)}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.quantity &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.quantity
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.quantity &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.quantity
                                                      }
                                                      inputProps={{ min: 1 }}
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      color={
                                                        theme.palette.custom
                                                          .productTextColor
                                                      }
                                                      fontSize={
                                                        theme.typography.regular
                                                          .fontSize
                                                      }
                                                      startAdornment={
                                                        <InputAdornment
                                                          position="start"
                                                          sx={{
                                                            pl: 2,
                                                            pr: 0.5,
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            mt: "1px",
                                                          }}
                                                        >
                                                          <span
                                                            style={{
                                                              fontSize:
                                                                "15px",
                                                              opacity: 0.7,
                                                              fontWeight: 500,
                                                            }}
                                                          >
                                                            ₹
                                                          </span>
                                                        </InputAdornment>
                                                      }
                                                      type="number"
                                                      placeholder="Rate"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].approx_rate`}
                                                      value={prod.approx_rate}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.approx_rate &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.approx_rate
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.approx_rate &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.approx_rate
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableActionCell>
                                                    <StyleTableActionButtonWrap>
                                                      <StyleTableIconButton
                                                        color="error"
                                                        disabled={!!itemId}
                                                        onClick={() => !itemId && remove(prodIndex)}
                                                      >
                                                        <Delete />
                                                      </StyleTableIconButton>
                                                    </StyleTableActionButtonWrap>
                                                  </StyleTableActionCell>
                                                </TableRow>
                                              );
                                              })}
                                                <TableRow>
                                                  <TableCell colSpan={5} sx={{ borderBottom: 'none' }}>
                                                    <Button
                                                      size="small"
                                                      startIcon={<Add />}
                                                      onClick={() =>
                                                        push({
                                                          product_name: "",
                                                          quantity: "",
                                                          approx_rate: "",
                                                          product_description: "",
                                                        })
                                                      }
                                                     style={{display: itemId ? "none" : undefined}}>
                                                      Add another product item
                                                    </Button>
                                                  </TableCell>
                                                </TableRow>
                                            </>
                                          )}
                                        </FieldArray>
                                      </TableBody>
                                    </StyleTable>
                                    </Box>
                                  </StyledPersonalShopperFieldGrid>
                                </StyleBoxContainer>
                              )}
                              {active === "online_shopping" && (
                                <StyleBoxContainer>
                                  <Grid container columnSpacing={2}>
                                    {(ind !==0 ) && (
                                      <Grid
                                        size={{
                                          lg: 2,
                                          md: 2,
                                          sm: 2,
                                          xs: 12,
                                        }}
                                      >
                                          <IconButtonStyle
                                            color="error"
                                            onClick={() => remove(ind)}
                                          >
                                            <Delete />
                                          </IconButtonStyle>
                                      </Grid>
                                    )}
                                    {/* Products FieldArray */}
                                    <Box
                                      sx={{
                                        width: "100%",
                                        overflowX: "auto",
                                        pb: 1,
                                      }}
                                    >
                                      <StyleTable
                                        sx={{
                                          minWidth: 1280,
                                          "& .MuiTableCell-root": {
                                            whiteSpace: "nowrap",
                                          },
                                        }}
                                      >
                                        <StyleTableHead>
                                          <TableRow>
                                            <TableCell width="16%" sx={{ minWidth: 150 }}>Product Name</TableCell>
                                            <TableCell width="10%" sx={{ minWidth: 100 }}>Quantity</TableCell>
                                            <TableCell width="14%" sx={{ minWidth: 150 }}>Online store name</TableCell>
                                            <TableCell width="18%" sx={{ minWidth: 180 }}>Product Link</TableCell>
                                            <TableCell width="14%" sx={{ minWidth: 145 }}>Online Order Number</TableCell>
                                            <TableCell width="10%" sx={{ minWidth: 110 }}>Order screenshot / PDF</TableCell>
                                            <TableCell width="12%" sx={{ minWidth: 150 }}>Exp. Delivery Date</TableCell>
                                            <StyleTableActionHeaderCell>
                                              Action
                                            </StyleTableActionHeaderCell>
                                          </TableRow>
                                        </StyleTableHead>
                                      <TableBody>
                                        <FieldArray name={`items[${ind}].products`}>
                                          {({ push, remove }) => (
                                            <>
                                              {products?.map((prod, prodIndex) => {
                                              return (
                                                <TableRow key={prodIndex}>
                                                  <StyleTableCell sx={{ minWidth: 170 }}>
                                                    <StyleTableFieldWrap>
                                                      <CustomInput
                                                        tableField
                                                        placeholder="Product Name"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        name={`items[${ind}].products[${prodIndex}].product_name`}
                                                        value={prod.product_name}
                                                        error={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.product_name &&
                                                          Boolean(
                                                            errors.items?.[ind]?.products?.[prodIndex]?.product_name
                                                          )
                                                        }
                                                        helperText={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.product_name &&
                                                          errors.items?.[ind]?.products?.[prodIndex]?.product_name
                                                        }
                                                      />
                                                    </StyleTableFieldWrap>
                                                  </StyleTableCell>
                                                  <StyleTableCell sx={{ minWidth: 120 }}>
                                                    <StyleTableFieldWrap>
                                                      <CustomInput
                                                        tableField
                                                        type="number"
                                                        placeholder="Qty"
                                                        onBlur={handleBlur}
                                                        name={`items[${ind}].products[${prodIndex}].quantity`}
                                                        value={prod.quantity}
                                                        onChange={(e) => setFieldValue(`items[${ind}].products[${prodIndex}].quantity`, e.target.value)}
                                                        error={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.quantity &&
                                                          Boolean(
                                                            errors.items?.[ind]?.products?.[prodIndex]?.quantity
                                                          )
                                                        }
                                                        helperText={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.quantity &&
                                                          errors.items?.[ind]?.products?.[prodIndex]?.quantity
                                                        }
                                                        inputProps={{ min: 1 }}
                                                      />
                                                    </StyleTableFieldWrap>
                                                  </StyleTableCell>
                                                  <StyleTableCell sx={{ minWidth: 180 }}>
                                                    <StyleTableFieldWrap>
                                                      <CustomDropdown
                                                        tableField
                                                        options={ecommercePlatformOptions}
                                                        placeholder="store name"
                                                        onBlur={handleBlur}
                                                        setFieldValue={setFieldValue}
                                                        name={`items[${ind}].products[${prodIndex}].source_name`}
                                                        value={prod.source_name}
                                                        error={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.source_name &&
                                                          Boolean(
                                                            errors.items?.[ind]?.products?.[prodIndex]?.source_name
                                                          )
                                                        }
                                                        helperText={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.source_name &&
                                                          errors.items?.[ind]?.products?.[prodIndex]?.source_name
                                                        }
                                                      />
                                                    </StyleTableFieldWrap>
                                                  </StyleTableCell>
                                                  <StyleTableCell sx={{ minWidth: 180 }}>
                                                    <StyleTableFieldWrap>
                                                      <CustomInput
                                                        tableField
                                                        type="text"
                                                        placeholder="Product Link"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        name={`items[${ind}].products[${prodIndex}].product_url`}
                                                        value={prod.product_url}
                                                        error={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.product_url &&
                                                          Boolean(
                                                            errors.items?.[ind]?.products?.[prodIndex]?.product_url
                                                          )
                                                        }
                                                        helperText={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.product_url &&
                                                          errors.items?.[ind]?.products?.[prodIndex]?.product_url
                                                        }
                                                      />
                                                    </StyleTableFieldWrap>
                                                  </StyleTableCell>
                                                  <StyleTableCell sx={{ minWidth: 145 }}>
                                                    <StyleTableFieldWrap>
                                                      <CustomInput
                                                        tableField
                                                        type="text"
                                                        placeholder="Online Order Number"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        name={`items[${ind}].products[${prodIndex}].order_id`}
                                                        value={prod.order_id}
                                                        error={
                                                          touched.items?.[ind]
                                                            ?.products?.[prodIndex]
                                                            ?.order_id &&
                                                          Boolean(
                                                            errors.items?.[ind]
                                                              ?.products?.[prodIndex]
                                                              ?.order_id
                                                          )
                                                        }
                                                        helperText={
                                                          touched.items?.[ind]
                                                            ?.products?.[prodIndex]
                                                            ?.order_id &&
                                                          errors.items?.[ind]
                                                            ?.products?.[prodIndex]
                                                            ?.order_id
                                                        }
                                                      />
                                                    </StyleTableFieldWrap>
                                                  </StyleTableCell>
                                                  <StyleTableCell sx={{ minWidth: 155, maxWidth: 155 }}>
                                                    <StyleTableFieldWrap>
                                                      <StyledUploadField
                                                        hasfile={prod.order_proof_url ? 1 : 0}
                                                      >
                                                        <input
                                                          type="file"
                                                          accept="image/*,.pdf,application/pdf"
                                                          id={`order-proof-${ind}-${prodIndex}`}
                                                          style={{ display: "none" }}
                                                          onChange={(e) =>
                                                            handleOrderProofUpload(
                                                              e,
                                                              ind,
                                                              prodIndex,
                                                              setFieldValue
                                                            )
                                                          }
                                                        />
                                                        <label
                                                          htmlFor={`order-proof-${ind}-${prodIndex}`}
                                                          style={{
                                                            cursor: "pointer",
                                                            width: "100%",
                                                            display: "block",
                                                          }}
                                                        >
                                                          <StyledUploadButton
                                                            component="span"
                                                            variant="outlined"
                                                            fullWidth
                                                            hasfile={prod.order_proof_url ? 1 : 0}
                                                            startIcon={
                                                              proofUploadKey === `${ind}-${prodIndex}` ? null : <CloudUploadOutlinedIcon fontSize="small" />
                                                            }
                                                            disabled={
                                                              proofUploadKey ===
                                                                `${ind}-${prodIndex}`
                                                            }
                                                          >
                                                            {proofUploadKey ===
                                                            `${ind}-${prodIndex}` ? (
                                                              <CircularProgress size={16} />
                                                            ) : (
                                                              prod.order_proof_url ? "File uploaded" : "Upload data"
                                                            )}
                                                          </StyledUploadButton>
                                                        </label>
                                                      </StyledUploadField>
                                                    </StyleTableFieldWrap>
                                                  </StyleTableCell>
                                                  <StyleTableCell sx={{ minWidth: 150 }}>
                                                    <StyleTableFieldWrap>
                                                      <CustomInput
                                                        tableField
                                                        required
                                                        type="date"
                                                        name={`items[${ind}].products[${prodIndex}].estimated_delivery`}
                                                        value={prod.estimated_delivery}
                                                        min={minimumTodayDateValidation()}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.estimated_delivery &&
                                                          Boolean(
                                                            errors.items?.[ind]?.products?.[prodIndex]?.estimated_delivery
                                                          )
                                                        }
                                                        helperText={
                                                          touched.items?.[ind]?.products?.[prodIndex]?.estimated_delivery &&
                                                          errors.items?.[ind]?.products?.[prodIndex]?.estimated_delivery
                                                        }
                                                      />
                                                    </StyleTableFieldWrap>
                                                  </StyleTableCell>
                                                  <StyleTableActionCell>
                                                    <StyleTableActionButtonWrap>
                                                      <StyleTableIconButton
                                                        color="error"
                                                        disabled={!!itemId}
                                                        onClick={() => !itemId && remove(prodIndex)}
                                                      >
                                                        <Delete />
                                                      </StyleTableIconButton>
                                                    </StyleTableActionButtonWrap>
                                                  </StyleTableActionCell>
                                                </TableRow>
                                              );
                                              })}
                                                <TableRow>
                                                  <TableCell colSpan={8} sx={{ borderBottom: 'none' }}>
                                                    <Button
                                                      size="small"
                                                      startIcon={<Add />}
                                                      onClick={() =>
                                                        push({
                                                          product_name: "",
                                                          quantity: "",
                                                          source_name: "",
                                                          product_url: "",
                                                          order_id: "",
                                                          order_proof_url: "",
                                                          estimated_delivery: ""
                                                        })
                                                      }
                                                     style={{display: itemId ? "none" : undefined}}>
                                                      Add another product item
                                                    </Button>
                                                  </TableCell>
                                                </TableRow>
                                            </>
                                            )}
                                        </FieldArray>
                                      </TableBody>
                                    </StyleTable>
                                    </Box>
                                  </Grid>
                                </StyleBoxContainer>
                              )}
                              {active === "pick_up" && (
                                <StyleBoxContainer>
                                  <StyledPersonalShopperFieldGrid
                                    container
                                    columnSpacing={1}
                                    rowSpacing={1}
                                  >
                                    <StyledAddressFieldGrid size={PU_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        required
                                        customLabel="Contact Person Name"
                                        placeholder="Contact Person Name"
                                        name={`items[${ind}].contact_person`}
                                        value={data.contact_person}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched?.items?.[ind]
                                            ?.contact_person &&
                                          Boolean(
                                            errors?.items?.[ind]?.contact_person
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.contact_person &&
                                          errors?.items?.[ind]?.contact_person
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PU_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        customLabel="Phone Number"
                                        placeholder="Phone Number"
                                        name={`items[${ind}].contact_mobile`}
                                        value={data.contact_mobile}
                                        onChange={(e) => {
                                          const val = e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 10);
                                          setFieldValue(
                                            `items[${ind}].contact_mobile`,
                                            val
                                          );
                                        }}
                                        onBlur={handleBlur}
                                        isPhoneNumber
                                        currentCountryCode={currentCountryCode}
                                        setCurrentCountryCode={setCurrentCountryCode}
                                        error={
                                          touched?.items?.[ind]
                                            ?.contact_mobile &&
                                          Boolean(
                                            errors?.items?.[ind]?.contact_mobile
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.contact_mobile &&
                                          errors?.items?.[ind]?.contact_mobile
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PU_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        required
                                        customLabel="Pick Up Address"
                                        placeholder="Pick Up Address"
                                        name={`items[${ind}].pickup_address`}
                                        value={data.pickup_address}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched?.items?.[ind]
                                            ?.pickup_address &&
                                          Boolean(
                                            errors?.items?.[ind]?.pickup_address
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.pickup_address &&
                                          errors?.items?.[ind]?.pickup_address
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <CountryStateFields
                                      fieldPrefix={`items[${ind}]`}
                                      hideCountry
                                      countryKey="pickup_country"
                                      stateKey="pickup_state"
                                      cityKey="pickup_city"
                                      countryLabel="Pickup Country"
                                      stateLabel="Pickup State"
                                      cityLabel="Pickup City"
                                      formGrid
                                      muiGridRow
                                      gridCellSize={PICKUP_LOCATION_GRID}
                                      countries={countries}
                                      loadingCountries={loadingCountries}
                                      getStatesForCountry={getStatesForCountry}
                                      getCitiesForCountryState={getCitiesForCountryState}
                                      values={values}
                                      errors={errors}
                                      touched={touched}
                                      setFieldValue={setFieldValue}
                                      handleBlur={handleBlur}
                                    />
                                    <StyledAddressFieldGrid size={PU_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        required
                                        customLabel="Pick Up Landmark"
                                        placeholder="Pick Up Landmark"
                                        name={`items[${ind}].pickup_landmark`}
                                        value={data.pickup_landmark}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched?.items?.[ind]
                                            ?.pickup_landmark &&
                                          Boolean(
                                            errors?.items?.[ind]?.pickup_landmark
                                          )
                                        }
                                        helperText={
                                          touched?.items?.[ind]
                                            ?.pickup_landmark &&
                                          errors?.items?.[ind]?.pickup_landmark
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PU_FIELD_GRID}>
                                      <CustomDropdown
                                        isprofile
                                        formGrid
                                        required
                                        options={selfDropTimeOptions}
                                        customLabel="Time"
                                        placeholder="Select time"
                                        name={`items[${ind}].pickup_time`}
                                        value={data.pickup_time}
                                        onBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        error={
                                          touched.items?.[ind]
                                            ?.pickup_time &&
                                          Boolean(
                                            errors.items?.[ind]
                                              ?.pickup_time
                                          )
                                        }
                                        helperText={
                                          touched.items?.[ind]
                                            ?.pickup_time &&
                                          errors.items?.[ind]
                                            ?.pickup_time
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    <StyledAddressFieldGrid size={PU_FIELD_GRID}>
                                      <CustomInput
                                        isprofile="true"
                                        formGrid
                                        required
                                        type="date"
                                        customLabel="Preferred Pickup Date"
                                        min={minimumTodayDateValidation()}
                                        name={`items[${ind}].pickup_date`}
                                        value={data.pickup_date}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched.items?.[ind]
                                            ?.pickup_date &&
                                          Boolean(
                                            errors.items?.[ind]
                                              ?.pickup_date
                                          )
                                        }
                                        helperText={
                                          touched.items?.[ind]
                                            ?.pickup_date &&
                                          errors.items?.[ind]
                                            ?.pickup_date
                                        }
                                      />
                                    </StyledAddressFieldGrid>
                                    {(ind !== 0) && (
                                      <Grid
                                        size={{
                                          lg: 2,
                                          md: 2,
                                          sm: 2,
                                          xs: 12,
                                        }}
                                      >
                                        <IconButtonStyle
                                          color="error"
                                          onClick={() => remove(ind)}
                                        >
                                          <Delete />
                                        </IconButtonStyle>
                                      </Grid>
                                    )}
                                    <Grid size={{ xs: 12 }}>
                                    <Box sx={{ width: "100%", overflowX: "auto" }}>
                                      <StyleTable sx={{ minWidth: 800 }}>
                                        <StyleTableHead>
                                          <TableRow>
                                            <TableCell width="25%">Product Name</TableCell>
                                            <TableCell width="15%">Quantity</TableCell>
                                            <TableCell width="20%">Weight</TableCell>
                                            <TableCell width="30%">Product Description</TableCell>
                                            <StyleTableActionHeaderCell>
                                              Action
                                            </StyleTableActionHeaderCell>
                                          </TableRow>
                                        </StyleTableHead>
                                      <TableBody>
                                        <FieldArray name={`items[${ind}].products`}>
                                          {({ push, remove }) => (
                                            <>
                                              {products?.map((prod, prodIndex) => {
                                              // const prodErrors =
                                              //   dropErrors.products?.[prodIndex] || {};
                                              // const prodTouched =
                                              //   dropTouched.products?.[prodIndex] || {};
                                              return (
                                                <TableRow key={prodIndex}>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      color={
                                                        theme.palette.custom
                                                          .productTextColor
                                                      }
                                                      fontSize={
                                                        theme.typography.regular.fontSize
                                                      }
                                                      height="10px"
                                                      placeholder="Product Name"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].product_name`}
                                                      value={prod.product_name}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_name &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.product_name
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_name &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.product_name
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      type="number"
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].quantity`}
                                                      value={prod.quantity}
                                                      onChange={(e) => setFieldValue(`items[${ind}].products[${prodIndex}].quantity`, e.target.value)}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.quantity &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.quantity
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.quantity &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.quantity
                                                      }
                                                      inputProps={{ min: 1 }}
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      color={
                                                        theme.palette.custom
                                                          .productTextColor
                                                      }
                                                      endAdornment={
                                                        <InputAdornment position="end">
                                                          Kg
                                                        </InputAdornment>
                                                      }
                                                      fontSize={
                                                        theme.typography.regular.fontSize
                                                      }
                                                      height="10px"
                                                      type="text"
                                                      placeholder="Weight"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].weight`}
                                                      value={prod.weight}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.weight &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.weight
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.weight &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.weight
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableCell>
                                                    <CustomInput
                                                      tableField
                                                      color={
                                                        theme.palette.custom
                                                          .productTextColor
                                                      }
                                                      fontSize={
                                                        theme.typography.regular.fontSize
                                                      }
                                                      height="10px"
                                                      type="text"
                                                      placeholder="Product Description"
                                                      onChange={handleChange}
                                                      onBlur={handleBlur}
                                                      name={`items[${ind}].products[${prodIndex}].product_description`}
                                                      value={prod.product_description}
                                                      error={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_description &&
                                                        Boolean(
                                                          errors.items?.[ind]?.products?.[prodIndex]?.product_description
                                                        )
                                                      }
                                                      helperText={
                                                        touched.items?.[ind]?.products?.[prodIndex]?.product_description &&
                                                        errors.items?.[ind]?.products?.[prodIndex]?.product_description
                                                      }
                                                    />
                                                  </StyleTableCell>
                                                  <StyleTableActionCell>
                                                    <StyleTableActionButtonWrap>
                                                      <StyleTableIconButton
                                                        color="error"
                                                        disabled={!!itemId}
                                                        onClick={() => !itemId && remove(prodIndex)}
                                                      >
                                                        <Delete />
                                                      </StyleTableIconButton>
                                                    </StyleTableActionButtonWrap>
                                                  </StyleTableActionCell>
                                                </TableRow>
                                              );
                                              })}
                                                <TableRow>
                                                  <TableCell colSpan={5} sx={{ borderBottom: 'none' }}>
                                                    <Button
                                                      size="small"
                                                      startIcon={<Add />}
                                                      onClick={() =>
                                                        push({
                                                          product_name: "",
                                                          quantity: "",
                                                          weight: "",
                                                          product_description: "",
                                                        })
                                                      }
                                                     style={{display: itemId ? "none" : undefined}}>
                                                      Add another product item
                                                    </Button>
                                                  </TableCell>
                                                </TableRow>
                                            </>
                                          )}
                                        </FieldArray>
                                      </TableBody>
                                    </StyleTable>
                                    </Box>
                                    </Grid>
                                  </StyledPersonalShopperFieldGrid>
                                </StyleBoxContainer>
                              )}
                            </Box>
                          {values?.items?.length - 1 === ind && (
                            <>
                              {!itemId && <StyledBoxButton>
                                <CustomButton
                                  padding="10px 18px"
                                  fontSize={theme.typography.base.fontSize}
                                  startIcon={<AddRound />}
                                  btntype="white"
                                  color={theme.palette.custom.warning}
                                  border="1px solid #FF8500"
                                  btnLabel="Add Another Item"
                                  onClick={() => {
                                    switch (values?.fieldType) {
                                      case "self_drop":
                                        push(selfDropItems?.[0])
                                        break;
                                      case "personal_shopper":
                                        push(personalShopperItems?.[0])
                                        break;
                                      case "online_shoping":
                                        push(shoppingItems?.[0])
                                        break;
                                      case "pick_up":
                                        push(pickupItems?.[0])
                                        break;
                                    
                                      default:
                                        push(selfDropItems?.[0])
                                        break;
                                    }
                                  }}
                                />
                              </StyledBoxButton>}

                              <StyledBoxWrapperButton>
                                <CustomButton
                                  type="button"
                                  padding="12px 80px"
                                  fontSize={theme.typography.base.fontSize}
                                  height="54px"
                                  btnLabel={
                                    isLockerSubmitting ? "Submitting..." : "Submit"
                                  }
                                  btntype={"primary"}
                                  color={theme.palette.custom.white}
                                  fontWeight={
                                    theme.typography.fontWeight.medium
                                  }
                                  border={theme.borders.defaultButton}
                                  disabled={isLockerSubmitting}
                                  isLoading={isLockerSubmitting}
                                  onClick={handleSubmit}
                                />
                              </StyledBoxWrapperButton>
                            </>
                          )}
                          {values?.items?.length - 1 !== ind && (
                            <StyledDivider />
                          )}
                        {/* </Box> */}
                        {/* {values?.items?.length - 1 !== ind && <Divider />} */}
                      </React.Fragment>
                    );
                  })}
                </StyledMain>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
    </StyledBoxV2>
  );
}
